// @ts-nocheck
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import getNumberTextField from "@store/Framework/Common/Fields/Toolkit/numberText";
import getSourceTypeField, { SourceType, sourceTypeOptions } from "@store/Framework/Common/Fields/Toolkit/sourceType";
import VideoComponent, { VideoPreloadValue, VideoTypeValue } from "@store/Library/Components/Toolkit/Video/Video";
import {
    videoAutoplayField,
    videoControlsField,
    videoHeightField,
    videoLoopField,
    videoMutedField,
    videoPosterField,
    videoPreloadField,
    videoSourceEmbedField,
    videoSourceMediaLibraryField,
    videoSourceUrlField,
    videoTypeField,
    videoWidthField,
} from "@store/Library/Components/Toolkit/Video/videoFields";
import useCurrentBreakpointValue from "@toolkit/Framework/Common/Hooks/useCurrentBreakpointValue";
import React, { FC } from "react";

const enum fields {
    autoplay = "autoplay",
    controls = "controls",
    height = "height",
    loop = "loop",
    muted = "muted",
    poster = "poster",
    preload = "preload",
    sourceEmbed = "sourceEmbed",
    sourceMediaLibrary = "sourceMediaLibrary",
    sourceUrl = "sourceUrl",
    sourceType = "sourceType",
    type = "type",
    width = "width",
}

const sourceTypeField = getSourceTypeField(fields.sourceType);
const heightField = getNumberTextField(fields.height, { integer: true });
const widthField = getNumberTextField(fields.width, { integer: true });

interface OwnProps extends WidgetProps {
    fields: {
        [fields.autoplay]: boolean;
        [fields.controls]: boolean;
        [fields.height]: string;
        [fields.loop]: boolean;
        [fields.muted]: boolean;
        [fields.poster]: string;
        [fields.preload]: VideoPreloadValue;
        [fields.sourceEmbed]: string;
        [fields.sourceMediaLibrary]: string;
        [fields.sourceUrl]: string;
        [fields.sourceType]: SourceType;
        [fields.type]: VideoTypeValue;
        [fields.width]: string;
    };
}

const Video: FC<OwnProps> = ({
    fields: {
        autoplay,
        controls,
        height: baseHeight,
        loop,
        muted,
        poster,
        preload,
        sourceEmbed,
        sourceUrl,
        sourceMediaLibrary,
        sourceType: baseSourceType,
        type,
        width: baseWidth,
    },
}) => {
    const sourceType = sourceTypeField.useParse(baseSourceType);
    const height = useCurrentBreakpointValue(heightField.useParse(baseHeight)) || undefined;
    const width = useCurrentBreakpointValue(widthField.useParse(baseWidth)) || undefined;
    const isMedia = sourceType === sourceTypeOptions.media.id;
    const isUrl = sourceType === sourceTypeOptions.url.id;
    const isEmbed = sourceType === sourceTypeOptions.embed.id;
    const { source } = isMedia
        ? { source: sourceMediaLibrary }
        : isUrl
          ? { source: sourceUrl }
          : { source: sourceEmbed };

    if (!source) {
        return null;
    }

    return (
        <VideoComponent
            autoPlay={autoplay}
            controls={controls}
            height={height}
            isEmbededIframe={isEmbed}
            loop={loop}
            muted={muted}
            poster={poster}
            preload={preload}
            src={source}
            type={type}
            width={width}
        />
    );
};

const settingsTab = {
    displayName: "Settings",
    sortOrder: 1,
};

const widgetModule: WidgetModule<Toolkit.FieldDefinition> = {
    component: Video,
    definition: {
        group: "Basic",
        displayName: "Video",
        icon: "video",
        fieldDefinitions: [
            {
                ...sourceTypeField.definition,
            },
            {
                ...videoSourceEmbedField,
                name: fields.sourceEmbed,
                isRequired: true,
                isVisible: x => x.fields.sourceType === sourceTypeOptions.embed.id,
            },
            {
                ...videoSourceUrlField,
                name: fields.sourceUrl,
                isRequired: true,
                isVisible: x => x.fields.sourceType === sourceTypeOptions.url.id,
            },
            {
                ...videoSourceMediaLibraryField,
                name: fields.sourceMediaLibrary,
                isRequired: true,
                isVisible: x => x.fields.sourceType === sourceTypeOptions.media.id,
            },
            {
                ...videoPosterField,
                name: fields.poster,
                isVisible: x =>
                    x.fields.sourceType === sourceTypeOptions.media.id ||
                    x.fields.sourceType === sourceTypeOptions.url.id,
            },
            {
                ...videoAutoplayField,
                name: fields.autoplay,
                tab: settingsTab,
                isVisible: x =>
                    x.fields.sourceType === sourceTypeOptions.media.id ||
                    x.fields.sourceType === sourceTypeOptions.url.id,
            },
            {
                ...videoControlsField,
                name: fields.controls,
                tab: settingsTab,
                isVisible: x =>
                    x.fields.sourceType === sourceTypeOptions.media.id ||
                    x.fields.sourceType === sourceTypeOptions.url.id,
            },
            {
                ...videoLoopField,
                name: fields.loop,
                tab: settingsTab,
                isVisible: x =>
                    x.fields.sourceType === sourceTypeOptions.media.id ||
                    x.fields.sourceType === sourceTypeOptions.url.id,
            },
            {
                ...videoMutedField,
                name: fields.muted,
                tab: settingsTab,
                isVisible: x =>
                    x.fields.sourceType === sourceTypeOptions.media.id ||
                    x.fields.sourceType === sourceTypeOptions.url.id,
            },
            {
                ...videoHeightField,
                ...heightField.definition,
                name: fields.height,
                tab: settingsTab,
            },
            {
                ...videoWidthField,
                ...widthField.definition,
                name: fields.width,
                tab: settingsTab,
            },
            {
                ...videoPreloadField,
                name: fields.preload,
                tab: settingsTab,
                isVisible: x =>
                    x.fields.sourceType === sourceTypeOptions.media.id ||
                    x.fields.sourceType === sourceTypeOptions.url.id,
            },
            {
                ...videoTypeField,
                name: fields.type,
                tab: settingsTab,
                isVisible: x =>
                    x.fields.sourceType === sourceTypeOptions.media.id ||
                    x.fields.sourceType === sourceTypeOptions.url.id,
            },
        ],
    },
};

export default widgetModule;
